import axios from 'axios';
import { useRouter } from 'next/router';
import { FC, FormEvent, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { AuthUser, PopupType, openAuthPopup, setAuthUser, setToken } from '../../store/auth';
import { Cookies, setCookie } from '../../utils';
import { Button, ButtonLink, Container, InputText, LinkButton, Spacing, Text } from '../ui';
import PasswordInputText from '../ui/PasswordInputText';
import { FlexContainer } from '../ui/Container';
import { Description } from '../ui/InputText/style';
import { Bottom } from './style';

const logIn = async (user: { email: string; password: string }) => {
  const response = await axios.post('/login', user);
  return response;
};

const verifyCode = async (user: { email: string; code: string }) => {
  const response = await axios.post('/verify-code', user);
  return response;
};

const INITIAL_STATE = {
  email: '',
  password: '',
  code: '',
};

const Login: FC = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const { mutateAsync: logInMutation, isLoading: isLogInLoading } = useMutation(logIn);
  const { mutateAsync: verifyCodeMutation, isLoading: isVerifyCodeLoading } = useMutation(verifyCode);
  const [values, setValues] = useState(INITIAL_STATE);

  const onChange = (e: FormEvent<HTMLInputElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setValues({ ...values, [name]: value });
  };

  const completeLogin = (token: string, user: AuthUser) => {
    dispatch(
      setAuthUser({
        ...user,
        isOnline: true,
      })
    );
    setCookie(Cookies.Token, token);
    dispatch(setToken(token));
    axios.defaults.headers.common = { Authorization: `bearer ${token}` };
    router.push('/');
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage('');
    try {
      if (verificationCodeSent) {
        const {
          data: { token, user },
        } = await verifyCodeMutation({ email: values.email, code: values.code });
        completeLogin(token, user);
      } else {
        const { email, password } = values;
        if (!email || !password) return setErrorMessage('Please enter your credentials before proceeding');
        const { data } = await logInMutation({ email, password });
        if (typeof data === 'object' && data?.token && data?.user) {
          completeLogin(data.token, data.user);
        } else {
          setVerificationCodeSent(true);
        }
      }
    } catch (error) {
      setErrorMessage(error?.response?.data);
    }
  };

  const doResetPassword = () => {
    dispatch(openAuthPopup(PopupType.Forgot_Password));
  };

  return (
    <>
      <Container paddingHorizontal="none" centered>
        <Description>
          {verificationCodeSent
            ? "We've sent a verification code to your email address. Please check your inbox and enter the code below to complete your login:"
            : 'Please input your email address and password to log in with your user account'}
        </Description>
        <Spacing top="xs" />
        <form onSubmit={onSubmit}>
          {verificationCodeSent ? (
            <InputText name="code" placeholder="Verification code" value={values.code} onChange={onChange} autoFocus />
          ) : (
            <>
              <InputText name="email" placeholder="Email" value={values.email} onChange={onChange} />
              <Spacing bottom="sm" />
              <PasswordInputText name="password" placeholder="Password" value={values.password} onChange={onChange} />
            </>
          )}
          {errorMessage && (
            <Spacing top="sm">
              <Text color="error">{errorMessage}</Text>
            </Spacing>
          )}
          <Spacing top="sm" />
          <FlexContainer gap="sm">
            {verificationCodeSent && (
              <Button
                type="button"
                text
                color="primary"
                fullWidth
                disabled={isVerifyCodeLoading}
                onClick={() => {
                  setVerificationCodeSent(false);
                  setErrorMessage('');
                }}
              >
                Go back
              </Button>
            )}
            <Button color="primary" fullWidth disabled={verificationCodeSent ? isVerifyCodeLoading : isLogInLoading}>
              {verificationCodeSent
                ? isVerifyCodeLoading
                  ? 'Submitting...'
                  : 'Submit'
                : isLogInLoading
                ? 'Logging In...'
                : 'Log In'}
            </Button>
          </FlexContainer>
          <Spacing top="sm" />
          <Bottom>
            <FlexContainer justify="center">
              <LinkButton size="xs" type="button" onClick={doResetPassword}>
                Forgot password?
              </LinkButton>
            </FlexContainer>
            <Spacing top="xxs" />
            <Text size="xs" color="textSecondary">
              Don&apos;t have an account?
            </Text>
            <ButtonLink padding="xxs" size="xs" href="/register">
              <Text size="xs" color="link">
                Sign up
              </Text>
            </ButtonLink>
          </Bottom>
        </form>
      </Container>
    </>
  );
};

export default Login;
